/** Small, deterministic helpers for positioning an HLS player in its window. */

// Keep this aligned with the live startup buffer used by liveLoadingProgress.
// The helper stays dependency-free so it can be exercised directly in Node.
const LIVE_BUFFER_SECONDS = 30
export const LIVE_MAX_LATENCY_SECONDS = 60
export const LIVE_SEEK_PADDING_SECONDS = 0.15

/**
 * Choose a point that is buffered far enough behind the live edge to survive
 * a late segment, while never returning a point before the current window.
 */
export function liveSyncTarget(
  seekableStart: number | null,
  seekableEnd: number | null,
  sync: number | null,
  bufferSeconds = LIVE_BUFFER_SECONDS,
  maxLatencySeconds = LIVE_MAX_LATENCY_SECONDS,
) {
  if (seekableEnd == null || !Number.isFinite(seekableEnd)) return sync
  const start = seekableStart != null && Number.isFinite(seekableStart) ? seekableStart : 0
  const safeEdge = Math.max(start, seekableEnd - bufferSeconds)
  if (sync == null || !Number.isFinite(sync) || sync < seekableEnd - maxLatencySeconds) return safeEdge
  return Math.min(Math.max(sync, start), safeEdge)
}

/** Keep the small seek-back cushion inside a sliding HLS window. */
export function safeLivePlaybackPosition(
  seekableStart: number | null,
  seekableEnd: number | null,
  target: number | null,
  paddingSeconds = LIVE_SEEK_PADDING_SECONDS,
) {
  if (target == null || !Number.isFinite(target)) return null
  if (seekableStart == null || !Number.isFinite(seekableStart)) return Math.max(0, target - paddingSeconds)
  const end = seekableEnd != null && Number.isFinite(seekableEnd) ? seekableEnd : Number.POSITIVE_INFINITY
  return Math.min(end, Math.max(seekableStart, target - paddingSeconds))
}
